/**
 * Persistent auth storage using IndexedDB for mobile app wrappers
 * where localStorage can be cleared by the OS.
 * 
 * Falls back to localStorage if IndexedDB is unavailable.
 */

const DB_NAME = 'mateo-auth';
const STORE_NAME = 'auth-store';
const DB_VERSION = 1;

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function idbGet(key: string): Promise<string | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const req = store.get(key);
    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror = () => reject(req.error);
  });
}

async function idbSet(key: string, value: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req = store.put(value, key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

async function idbRemove(key: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req = store.delete(key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

/**
 * A Supabase-compatible storage adapter backed by IndexedDB.
 * Supabase's auth system calls getItem/setItem/removeItem synchronously,
 * but also supports async returns. We write to both localStorage (sync fallback)
 * and IndexedDB (persistent) to ensure sessions survive OS-level cache clears.
 */
export const persistentStorage = {
  getItem: (key: string): string | null | Promise<string | null> => {
    // Try localStorage first (fast, synchronous)
    const localValue = localStorage.getItem(key);
    if (localValue) return localValue;

    // Fall back to IndexedDB (may have survived a localStorage clear)
    if (typeof indexedDB !== 'undefined') {
      return idbGet(key).then((idbValue) => {
        // Restore to localStorage for future fast access
        if (idbValue) {
          try { localStorage.setItem(key, idbValue); } catch {}
        }
        return idbValue;
      }).catch(() => null);
    }

    return null;
  },

  setItem: (key: string, value: string): void | Promise<void> => {
    // Always write to localStorage for synchronous access
    try { localStorage.setItem(key, value); } catch {}

    // Also persist to IndexedDB for durability
    if (typeof indexedDB !== 'undefined') {
      idbSet(key, value).catch(() => {});
    }
  },

  removeItem: (key: string): void | Promise<void> => {
    try { localStorage.removeItem(key); } catch {}

    if (typeof indexedDB !== 'undefined') {
      idbRemove(key).catch(() => {});
    }
  },
};
